import { Injectable } from '@angular/core';
import { WindowService } from '@cigna/core/window';
// TODO: https://git.sys.cigna.com/angular-guild/cigna-nx/issues/567
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/features';
import { ConversationsDataAccessService } from '@cigna/omni/data-access';
import { OmniLiveAvailabilityFacade } from '@cigna/omni/live-availability-state';
import {
  ConversationEventWithState,
  isBotCHEConversation,
  isConversation,
  isConversationEndInitiationEvent,
  isConversationTransferInitiationEvent,
} from '@cigna/omni/shared';
import {
  ConversationSummaryDTO,
  ConversationActivityEventDTO,
  ConversationDTO,
  ConversationMessageEventDTO,
} from '@cigna/vampire-dto';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { Action } from '@ngrx/store';
import { Observable, concat, of } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  distinct,
  distinctUntilKeyChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  throttle,
  withLatestFrom,
  distinctUntilChanged,
  timeout,
  finalize,
  tap,
  exhaustMap,
} from 'rxjs/operators';
import {
  ConversationDiff,
  ConversationsActionTypes,
  CreateConversation,
  CreateConversationFailure,
  CreateConversationSuccess,
  EndConversation,
  EndConversationFailure,
  EndConversationSuccess,
  TerminateConversation,
  GetConversation,
  GetConversationFailure,
  GetConversationSuccess,
  GetTransferredConversation,
  GetTransferredConversationFailure,
  GetTransferredConversationSuccess,
  LoadConversationsFailure,
  LoadConversationsSuccess,
  MessageViewed,
  MessageViewedFailure,
  MessageViewedSuccess,
  OnKeyUp,
  PostActivity,
  PostActivityFailure,
  PostActivitySuccess,
  PostCta,
  PostCtaFailure,
  PostCtaSuccess,
  PostMessage,
  PostMessageFailure,
  PostMessageSuccess,
  SetSelectedConversationId,
  TransferConversation,
  TransferConversationFailure,
  TransferConversationSuccess,
  BackToPrevious,
  GetActiveConversationSuccess,
  GetActiveConversationFailure,
  GetActiveConversation,
  GetConversationHistory,
  OpenConversation,
} from './conversations.actions';
import { OmniConversationsFacade } from './conversations.facade';

export function updateConversation(
  conversation: ConversationDTO,
): ConversationDTO {
  const transferManualIndex = conversation.events.findIndex(
    (event) =>
      (event as ConversationEventWithState).state === 'transfer-manual',
  );
  const lastMessageIndex = conversation.events
    .map(
      (event, index) => event.type === 'message' && index < transferManualIndex,
    )
    .lastIndexOf(true);
  return {
    ...conversation,
    events: conversation.events.map((event, index) =>
      index === lastMessageIndex
        ? {
            ...event,
            message:
              'Please submit your question so that your personal advocate may better assist you',
          }
        : event,
    ),
  };
}

@Injectable()
export class ConversationsEffects {
  createNewConversation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateConversation>(ConversationsActionTypes.CreateConversation),
      withLatestFrom(
        this.conversationsFacade.conversationMetadata$,
        this.liveAvailbilityFacade.liveChatAvailable$,
        this.conversationsFacade.conversationId$,
      ),
      filter(
        ([_payload, _metadata, isLiveChatAvailable, conversationID]) =>
          isLiveChatAvailable && !conversationID,
      ),
      exhaustMap(
        ([
          {
            payload: { type, message, context },
          },
          conversationMeta,
        ]) =>
          this.dataAccessService
            .postConversation(
              type,
              {
                ...context,
                tags: conversationMeta?.currentType
                  ? [conversationMeta.currentType]
                  : context.tags,
                meta: conversationMeta,
              },
              message,
            )
            .pipe(
              tap((conversation) =>
                this.conversationsFacade.setExistingConversationId(
                  conversation._id,
                ),
              ),
              map(
                (conversation) => new CreateConversationSuccess(conversation),
              ),
              catchError((error) => of(new CreateConversationFailure(error))),
            ),
      ),
    ),
  );

  setConversationId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<SetSelectedConversationId>(
        ConversationsActionTypes.SetSelectedConversationId,
      ),
      withLatestFrom(this.conversationsFacade.conversationEntities$),
      mergeMap(([{ payload }, entities]) => {
        const actions: Action[] = [
          new GetConversation(payload),
          new PostActivity('listening'),
        ];
        const selectedConversation = entities[payload];

        if (selectedConversation && selectedConversation.transferIds) {
          const transferIds = selectedConversation.transferIds.filter(
            (transferId) => !entities[transferId],
          );
          actions.push(
            ...transferIds.map(
              (transferId) => new GetTransferredConversation(transferId),
            ),
          );
        }
        return actions;
      }),
    ),
  );

  backToPreviousClick$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<BackToPrevious>(ConversationsActionTypes.BackToPrevious),
      withLatestFrom(this.conversationsFacade.isAiBot$),
      switchMap(([{ payload }, isAiBot]) =>
        this.dataAccessService.endBotConversation(payload.convId).pipe(
          switchMap((conversation) =>
            isAiBot
              ? [
                  new SetSelectedConversationId(''),
                  new EndConversationSuccess(conversation),
                  new CreateConversation({
                    type: 'bot',
                    context: payload.context,
                  }),
                ]
              : [
                  new SetSelectedConversationId(''),
                  new CreateConversation({
                    type: 'bot',
                    context: payload.context,
                  }),
                ],
          ),
        ),
      ),
    ),
  );

  getTransferredConversation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetTransferredConversation>(
        ConversationsActionTypes.GetTransferredConversation,
      ),
      mergeMap(({ payload }) =>
        this.dataAccessService.getBotConversation(payload).pipe(
          mergeMap((conversation) =>
            of(new GetTransferredConversationSuccess(conversation)),
          ),
          catchError((error) =>
            of(new GetTransferredConversationFailure(error)),
          ),
        ),
      ),
    ),
  );

  endConversation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EndConversation>(ConversationsActionTypes.EndConversation),
      withLatestFrom(this.conversationsFacade.conversationsEntities$),
      concatMap(([{ payload }, allConversations]) => {
        const isLiveConvo =
          allConversations?.[payload] &&
          allConversations[payload]?.type !== 'bot';
        return (
          isLiveConvo
            ? this.dataAccessService.endLiveConversation(payload)
            : this.dataAccessService.endBotConversation(payload)
        ).pipe(
          tap(() => {
            this.conversationsFacade.setExistingConversationId('');
          }),
          map((conversation) => new EndConversationSuccess(conversation)),
          catchError((error) => of(new EndConversationFailure(error))),
        );
      }),
    ),
  );

  terminateConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<TerminateConversation>(
          ConversationsActionTypes.TerminateConversation,
        ),
        withLatestFrom(
          this.conversationsFacade.conversation$,
          this.featuresFacade.featuresEnabled(['shouldEnableChat']),
        ),
        switchMap(([{ userAction, logoutUrl, ssoId }, convo, enableAwsChat]) =>
          (convo?._id
            ? enableAwsChat
              ? this.dataAccessService.terminateLambdaConversation(
                  convo._id,
                  ssoId || '',
                  userAction,
                  convo.adapter === 'bot' ? 'bot' : 'user',
                )
              : this.dataAccessService.terminateConversation(
                  convo._id,
                  userAction,
                )
            : of()
          ).pipe(
            timeout(3000),
            finalize(() => {
              if (logoutUrl) {
                this.window.location.href = logoutUrl;
              }
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  getConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetConversation>(ConversationsActionTypes.GetConversation),
      withLatestFrom(this.conversationsFacade.conversationsEntities$),
      filter(([{ payload }]) => !!payload),
      switchMap(([{ payload }, allConversations]) => {
        const isLiveConvo =
          allConversations?.[payload] &&
          allConversations?.[payload]?.type !== 'bot';
        return (
          isLiveConvo
            ? this.dataAccessService.getLiveConversation(payload)
            : this.dataAccessService.getBotConversation(payload)
        ).pipe(
          mergeMap((conversation) => {
            const ret: Action[] = [new GetConversationSuccess(conversation)];

            const oldConv = allConversations[payload];
            if (isConversation(oldConv)) {
              const newEvents = conversation.events.filter(
                (e) => e.created > oldConv.updated,
              );
              if (newEvents.length) {
                ret.push(
                  new ConversationDiff({
                    newEvents,
                    _id: payload,
                  }),
                );
              }
            }
            return ret;
          }),
          catchError((error) => of(new GetConversationFailure(error))),
        );
      }),
    ),
  );

  getActiveConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetActiveConversation>(
        ConversationsActionTypes.GetActiveConversation,
      ),
      withLatestFrom(this.featuresFacade.featuresEnabled(['shouldEnableChat'])),
      filter(([_data, shouldEnableChat]) => shouldEnableChat),
      switchMap(() =>
        this.dataAccessService.activeConversation().pipe(
          map((conversation) => new GetActiveConversationSuccess(conversation)),
          catchError((error) => of(new GetActiveConversationFailure(error))),
        ),
      ),
    ),
  );

  getActiveConversationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetActiveConversationSuccess>(
        ConversationsActionTypes.GetActiveConversationSuccess,
      ),
      filter(({ response }) => !!response?.nextConversation),
      switchMap(({ response }) => [
        new OpenConversation(true),
        new TransferConversationSuccess(response),
      ]),
    ),
  );

  getConversationTransferEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetConversationSuccess>(
        ConversationsActionTypes.GetConversationSuccess,
      ),
      withLatestFrom(
        this.liveAvailbilityFacade.liveChatAvailable$,
        this.featuresFacade.featuresEnabled(['liveClinicianChat']),
        this.featuresFacade.featuresEnabled(['asyncSupportChat']),
      ),
      filter(
        ([
          {
            payload: { events, context, type },
          },
          liveAvailable,
          liveCHEEnabled,
          asyncEnabled,
        ]) =>
          ((liveAvailable &&
            (!isBotCHEConversation(context, type) || liveCHEEnabled)) ||
            asyncEnabled) &&
          !!events?.some(
            (e) =>
              isConversationTransferInitiationEvent(e) ||
              isConversationEndInitiationEvent(e),
          ),
      ),
      distinct(
        ([
          {
            payload: { events },
          },
        ]) =>
          events.filter(
            (e) =>
              isConversationTransferInitiationEvent(e) ||
              isConversationEndInitiationEvent(e),
          )[0]._id,
      ),
      map(
        ([
          {
            payload: { events, initial, context, type, _id },
          },
          liveAvailable,
          liveCHEEnabled,
          asyncEnabled,
        ]) => ({
          transferId: events.find((e) =>
            isConversationTransferInitiationEvent(e),
          )?._id,
          endId: events.find((e) => isConversationEndInitiationEvent(e))?._id,
          _id,
          liveAvailable,
          liveCHEEnabled,
          asyncEnabled,
          initial,
          context,
          type,
        }),
      ),
      map(
        ({
          initial,
          context,
          type,
          liveAvailable,
          liveCHEEnabled,
          _id,
          transferId,
        }) => {
          if (transferId) {
            return new TransferConversation({
              initial,
              conversationId: _id,
              type:
                liveAvailable &&
                (!isBotCHEConversation(context, type) || liveCHEEnabled)
                  ? 'live'
                  : 'async',
            });
          }
          return new EndConversation(_id);
        },
      ),
    ),
  );

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationsActionTypes.LoadConversations),
      switchMap(() =>
        this.dataAccessService.loadConversations().pipe(
          map((conversations) => new LoadConversationsSuccess(conversations)),
          catchError((error) => of(new LoadConversationsFailure(error))),
        ),
      ),
    ),
  );

  loadDMDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationsActionTypes.LoadConversationsSuccess),
      withLatestFrom(this.conversationsFacade.unreadOpenDmConversations$),
      switchMap(([_, dms]) => dms),
      concatMap(({ _id }) =>
        this.dataAccessService.getLiveConversation(_id).pipe(
          map((conversation) => new GetConversationSuccess(conversation)),
          catchError((error) => of(new GetConversationFailure(error))),
        ),
      ),
    ),
  );

  messageViewed$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<MessageViewed>(ConversationsActionTypes.MessageViewed),
      filter(({ payload }) => !!payload && payload !== 'noop'),
      distinctUntilKeyChanged('payload'),
      withLatestFrom(
        this.conversationsFacade.conversationId$,
        this.conversationsFacade.conversationEntities$,
      ),
      filter(([_payload, conversationId, _conversations]) => !!conversationId),
      switchMap(([{ payload }, conversationId, conversations]) => {
        const isLiveId =
          conversations?.[conversationId as string] &&
          conversations?.[conversationId as string]?.type !== 'bot';
        return (
          isLiveId
            ? this.dataAccessService.liveMessageViewed(
                conversationId as string,
                payload,
              )
            : this.dataAccessService.botMessageViewed(
                conversationId as string,
                payload,
              )
        ).pipe(
          map((newMessages) =>
            newMessages.length > 0
              ? new GetConversation(conversationId as string)
              : new MessageViewedSuccess({
                  conversationId: conversationId as string,
                  eventId: payload,
                }),
          ),
          catchError((error) => of(new MessageViewedFailure(error))),
        );
      }),
    ),
  );

  onKeyUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<OnKeyUp | PostMessageSuccess>(
        ConversationsActionTypes.OnKeyUp,
        ConversationsActionTypes.PostMessageSuccess,
      ),
      withLatestFrom(this.conversationsFacade.activityDebounceTime$),
      filter(([, activityDebounceTime]) => activityDebounceTime > 0),
      switchMap(([{ type }, activityDebounceTime]) => {
        switch (type) {
          case ConversationsActionTypes.PostMessageSuccess:
            return of('reset');
          case ConversationsActionTypes.OnKeyUp:
            return concat(
              of('responding'),
              of('listening').pipe(delay(activityDebounceTime)),
            );
        }
      }),
      distinctUntilChanged(),
      filter(
        (type): type is ConversationActivityEventDTO['activityType'] =>
          type !== 'reset',
      ),
      map((type) => new PostActivity(type)),
    ),
  );

  postActivity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<PostActivity>(ConversationsActionTypes.PostActivity),
      withLatestFrom(this.conversationsFacade.conversation$),
      filter(
        (c): c is [PostActivity, ConversationSummaryDTO] =>
          !!c[1] && c[1].type === 'live' && c[1].state !== 'closed',
      ),
      switchMap(([{ activityType }, conversation]) =>
        this.dataAccessService
          .postActivity(conversation._id, activityType)
          .pipe(
            map((res) => new PostActivitySuccess(res)),
            catchError((e) => of(new PostActivityFailure(e))),
          ),
      ),
    ),
  );

  postCta$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<PostCta>(ConversationsActionTypes.PostCta),
      withLatestFrom(this.conversationsFacade.conversationId$),
      throttle(() =>
        this.actions$.pipe(
          ofType<PostCtaSuccess | PostCtaFailure>(
            ConversationsActionTypes.PostCtaSuccess,
            ConversationsActionTypes.PostCtaFailure,
          ),
        ),
      ),
      filter((c): c is [PostCta, string] => !!c[1]),
      concatMap(
        ([
          {
            payload: { sourceMessageId, value, _id },
          },
          conversationId,
        ]) =>
          this.dataAccessService
            .postCta(conversationId, sourceMessageId, value, _id)
            .pipe(
              map(
                (messageEvent) =>
                  new PostCtaSuccess({ conversationId, messageEvent }),
              ),
              catchError((error) => of(new PostCtaFailure(error))),
            ),
      ),
    ),
  );

  postCtaSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationsActionTypes.PostCtaSuccess),
      withLatestFrom(
        this.featuresFacade.featuresEnabled(['shouldEnableChat']),
        this.conversationsFacade.conversationEntities$,
      ),
      filter(
        ([{ payload }, feature, conversations]: [
          {
            payload: {
              conversationId: string;
              messageEvent: ConversationMessageEventDTO;
            };
          },
          boolean,
          Dictionary<ConversationDTO | ConversationSummaryDTO>,
        ]) =>
          !!(
            payload?.conversationId &&
            (!conversations?.[payload.conversationId] ||
              conversations[payload.conversationId]?.state?.toLowerCase() !==
                'closed') &&
            feature
          ),
      ),
      map(([res]) => new GetConversation(res.payload.conversationId)),
    ),
  );

  conversationHistory$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetConversationHistory>(
        ConversationsActionTypes.GetConversationHistory,
      ),
      withLatestFrom(this.featuresFacade.featuresEnabled(['shouldEnableChat'])),
      filter(([_data, shouldEnableChat]) => shouldEnableChat),
      switchMap(() =>
        this.dataAccessService.conversationHistory().pipe(
          map((payload) => new LoadConversationsSuccess(payload)),
          catchError((error) => of(new LoadConversationsFailure(error))),
        ),
      ),
    ),
  );

  postNewMessage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<PostMessage>(ConversationsActionTypes.PostMessage),
      withLatestFrom(this.conversationsFacade.conversationId$),
      filter((c): c is [PostMessage, string] => !!c[0].payload && !!c[1]),
      concatMap(([{ payload }, conversationId]) =>
        this.dataAccessService.postMessage(conversationId, payload).pipe(
          map(
            (messageEvent) =>
              new PostMessageSuccess({ conversationId, messageEvent }),
          ),
          catchError((error) => of(new PostMessageFailure(error))),
        ),
      ),
    ),
  );

  // @Effect()
  // initiateTransferCall$: Observable<Action> = this.actions$.pipe(
  //   ofType<PostMessage>(ConversationsActionTypes.PostMessage),
  //   withLatestFrom(
  //     this.conversationsFacade.conversationId$,
  //     this.conversationsFacade.allConversations$,
  //   ),
  //   filter(
  //     (
  //       c,
  //     ): c is [
  //       PostMessage,
  //       string,
  //       Array<ConversationDTO | ConversationSummaryDTO>,
  //     ] =>
  //       !!c[0].payload &&
  //       !!c[1] &&
  //       !(c[2].find(
  //         (convo) => convo._id === c[1],
  //       ) as ConversationDTO)?.events.some(
  //         (event) => (event as ConversationEventWithState).state === 'transfer',
  //       ),
  //   ),
  //   map(
  //     ([{ payload }, conversationId]) =>
  //       new TransferConversation({
  //         conversationId,
  //         type: 'live',
  //         initial: payload,
  //       }),
  //   ),
  // );

  transferConversation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<TransferConversation>(
        ConversationsActionTypes.TransferConversation,
      ),
      switchMap(({ payload: { conversationId, type, initial } }) =>
        this.dataAccessService
          .transferConversation(conversationId, type, initial)
          .pipe(
            map((result) => new TransferConversationSuccess(result)),
            catchError((error) => of(new TransferConversationFailure(error))),
          ),
      ),
    ),
  );

  constructor(
    private window: WindowService,
    public actions$: Actions,
    public conversationsFacade: OmniConversationsFacade,
    public dataAccessService: ConversationsDataAccessService,
    public liveAvailbilityFacade: OmniLiveAvailabilityFacade,
    public featuresFacade: FeatureTogglesFacade,
  ) {}
}
